import React, { useContext, useState } from "react";
import { PlusCircle, X, CheckCircle2, Tags } from "lucide-react";
import useGlobalInventory from "../../../services/Inventory/useGlobalInventory";
import useInventory from "../../../services/Inventory/useInventory";
import { GlobalContext } from "../../../Context/GlobalContext";
import NoIndex from "../../../components/NoIndex";

const AddInventorySubCategoryModal = ({
  show,
  onClose,
  selectedCategoryName,
  onSuccess,
}) => {
  const { addInventorySubCategory } = useGlobalInventory();
  const { uploadItemImage } = useInventory();

  const { userID } = useContext(GlobalContext);

  const [form, setForm] = useState({
    subCategory: "",
    subCategoryImage: "",
  });

  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!show) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileToBase64 = (file) => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        subCategoryImage: reader.result,
      }));

      setPreview(reader.result);
    };

    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      let imageUrl = form.subCategoryImage;

      // If image is base64 then upload first
      if (form.subCategoryImage?.startsWith("data:")) {
        const uploadRes = await uploadItemImage({
          item_name: form.subCategory,
          image_data: form.subCategoryImage,
        });

        if (uploadRes?.status === "0") {
          imageUrl = uploadRes.image_url;
        }
      }

      const payload = {
        userid: userID,
        category: selectedCategoryName,
        subCategory: form.subCategory,
        subCategoryImage: imageUrl,
      };

      const res = await addInventorySubCategory(payload);

      if (res?.responseCode === "00") {
        onSuccess?.();
        onClose();

        // reset form
        setForm({
          subCategory: "",
          subCategoryImage: "",
        });

        setPreview("");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (

    <>
    <NoIndex/>

    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 montserrat-font-1">
      <div className="bg-white rounded-2xl shadow-xl w-[650px] max-w-[95%] montserrat-font-1">
        {/* HEADER */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div className="flex items-center gap-3 font-semibold text-gray-700">
            <div className="bg-green-100 p-2 rounded-lg">
              <PlusCircle size={18} className="text-green-600" />
            </div>
            Add Subcategory
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 border"
          >
            <X size={18} />
          </button>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} className="p-6 montserrat-font-1">
          <div className="border rounded-xl p-5 bg-gray-50">
            {/* SECTION HEADER */}
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
              <Tags size={16} />
              Details
              <span className="text-xs text-gray-400">
                Subcategory + optional image
              </span>
            </div>

            {/* GRID */}
            <div className="grid grid-cols-2 gap-4">
              {/* CATEGORY */}
              <div>
                <label className="text-xs font-medium text-gray-600">
                  Category (locked)
                </label>

                <input
                  type="text"
                  value={selectedCategoryName}
                  disabled
                  className="w-full mt-1 border rounded-lg px-3 py-2 bg-gray-100 text-sm"
                />
              </div>

              {/* SUBCATEGORY */}
              <div>
                <label className="text-xs font-medium text-gray-600">
                  Subcategory *
                </label>

                <input
                  type="text"
                  name="subCategory"
                  value={form.subCategory}
                  onChange={handleChange}
                  placeholder="e.g., Ketchup"
                  required
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* SECOND GRID */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              {/* IMAGE URL */}
              <div>
                <label className="text-xs font-medium text-gray-600">
                  Image URL
                </label>

                <input
                  type="text"
                  name="subCategoryImage"
                  value={form.subCategoryImage}
                  onChange={handleChange}
                  placeholder="https://... (optional)"
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* FILE UPLOAD */}
              <div>
                <label className="text-xs font-medium text-gray-600">
                  Upload Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileToBase64(e.target.files?.[0])}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                />

                <p className="text-[11px] text-gray-400 mt-1">
                  If you upload, it will be stored and saved as URL.
                </p>
              </div>
            </div>

            {/* PREVIEW */}
            {preview && (
              <div className="flex justify-center mt-4">
                <img src={preview} className="h-24 object-contain" />
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
            >
              <X size={16} />
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
            >
              <CheckCircle2 size={16} />
              {submitting ? "Adding..." : "Add Subcategory"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default AddInventorySubCategoryModal;
